import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { firstValueFrom, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ADPResourceService, ResourcesLoaderService } from 'digital-core';
import { PGHAppLoadService } from 'src/app/core/core';

/**
 * Allows navigation to /lookup-account only when Global_Login_Disabled is 'true'.
 * When the normal login flow is active the route is inaccessible and the user is
 * redirected to / instead.
 */
export const LookupAccountActivateGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const appLoadService = inject(PGHAppLoadService);
  const resourcesLoader = inject(ResourcesLoaderService);
  const resourceService = inject(ADPResourceService);
  const router = inject(Router);

  await ensureSiteResourcesLoaded(appLoadService, resourcesLoader, resourceService);

  const isLoginDisabled = resourceService.getPortalSetting('Global_Login_Disabled')?.Value?.toLowerCase()?.trim() === 'true';
  return isLoginDisabled ? true : router.createUrlTree(['/']);
};

/**
 * canActivate runs BEFORE the route resolvers (SiteConfigResolver / PageResourcesResolver),
 * so on a cold load / refresh the global settings + labels aren't populated yet.
 * We drive the load ourselves, mirroring the resolvers. Warm navigations short-circuit.
 * Failures fail-open so a load error never blocks navigation.
 */
async function ensureSiteResourcesLoaded(appLoadService: PGHAppLoadService, resourcesLoader: ResourcesLoaderService, resourceService: ADPResourceService): Promise<void> {
  if (appLoadService.isSiteResourcesLoaded.getValue()) {
    return;
  }
  try {
    await firstValueFrom(from(window['__bootstrapConfigPromise'] ?? Promise.resolve()).pipe(switchMap(() => appLoadService.loadSiteResources())));
    const pageResources = await firstValueFrom(resourcesLoader.loadAllPageResources('Global', false, true));
    resourceService.setGlobalResources(pageResources.resources);
    resourceService.setGlobalLabels(pageResources.labels);
    resourceService.setPortalPageList(pageResources.lists);
    appLoadService.isSiteResourcesLoaded.next(true);
  } catch {
    // Fail-open: if resources can't be loaded, let the user through rather than blank the page.
  }
}
